import { useRouter } from 'next/router';
import type { FC } from 'react';

import { Button, Space } from '@sravni/react-design-system';
import { Sheet } from '@sravni/react-design-system/lib/Sheet';
import { Text, Heading } from '@sravni/react-design-system/lib/Typography';
import { Image } from '@sravni/react-image';

import { PRODUCT_LIST_ANCHOR } from '@src/constants/common';
import { scrollToElementById } from '@src/helpers/common';
import { ToFullDealAdvertisingButton } from 'features/FullDeal';
import {
  FULL_DEAL_MOBILE_APP_LINKS,
  FULL_DEAL_MOBILE_APP_SOURCE,
  FULL_DEAL_WEB_TO_WEB_LINKS,
  FULL_DEAL_WEB_TO_WEB_SOURCE,
} from 'features/FullDeal/config';
import { buildHasOfferLinkForFullDeal } from 'features/FullDeal/lib';

import mobileAppSheetCat from './assets/mobileAppSheetCat.png';
import styles from './MobileAppConvenienceSheet.module.scss';
import { MobileAppConvenienceSheetTexts } from './MobileAppConvenienceSheet.texts';

type MobileAppConvenienceSheetProps = {
  isLandingPage: boolean;
  amount: number;
  term: number;
  visible: boolean;

  isUserFromGoogleSearch: boolean;

  onClose: (event: React.MouseEvent | KeyboardEvent | PopStateEvent) => void;
};

export const MobileAppConvenienceSheet: FC<MobileAppConvenienceSheetProps> = ({
  visible,
  onClose,
  isLandingPage,
  amount,
  term,
  isUserFromGoogleSearch,
}) => {
  const { asPath } = useRouter();

  const scrollToProductList = (e: React.MouseEvent) => {
    onClose(e);

    setTimeout(() => {
      scrollToElementById(PRODUCT_LIST_ANCHOR);
    }, 300);
  };

  return (
    <Sheet visible={visible} onClose={onClose}>
      <Sheet.Header
        title={
          <Space size={[16, 16]} direction="vertical" align="center">
            <Image src={mobileAppSheetCat} alt={MobileAppConvenienceSheetTexts.title} width={240} height={140} />
            <Heading level={3} size={28}>
              {MobileAppConvenienceSheetTexts.title}
            </Heading>
          </Space>
        }
        className={styles.header}
      />
      <Sheet.Content className={styles.content}>
        <Text className={styles.paragraph} size={16}>
          {MobileAppConvenienceSheetTexts.firstParagraph}
        </Text>
        <Text className={styles.paragraph} size={16}>
          {MobileAppConvenienceSheetTexts.secondParagraph}
        </Text>
        <Text className={styles.paragraph} size={16}>
          {MobileAppConvenienceSheetTexts.thirdParagraph}
        </Text>
        <Text className={styles.badge} size={14}>
          {MobileAppConvenienceSheetTexts.badge}
        </Text>
      </Sheet.Content>
      <Sheet.Footer className={styles.footer}>
        <ToFullDealAdvertisingButton
          size={52}
          block
          variant="primary"
          link={FULL_DEAL_MOBILE_APP_LINKS.FROM_CALCULATOR_BOTTOM_SHEET}
          source={FULL_DEAL_MOBILE_APP_SOURCE.FROM_CALCULATOR_BOTTOM_SHEET}
        >
          {MobileAppConvenienceSheetTexts.downloadApp}
        </ToFullDealAdvertisingButton>

        {isUserFromGoogleSearch ? (
          <Button variant="text" size={52} block onClick={scrollToProductList}>
            {MobileAppConvenienceSheetTexts.continueInWebsite}
          </Button>
        ) : (
          <ToFullDealAdvertisingButton
            as="link"
            variant="text"
            className={styles.continueButton}
            link={buildHasOfferLinkForFullDeal({
              link: FULL_DEAL_WEB_TO_WEB_LINKS.FROM_CALCULATOR_BOTTOM_SHEET,
              amount,
              term,
              showAuth: true,
              ...(isLandingPage && {
                showAuth: true,
                referrer: asPath,
              }),
            })}
            linkTarget="_self"
            size={52}
            source={FULL_DEAL_WEB_TO_WEB_SOURCE.FROM_CALCULATOR_BOTTOM_SHEET}
          >
            {MobileAppConvenienceSheetTexts.continueInWebsite}
          </ToFullDealAdvertisingButton>
        )}
      </Sheet.Footer>
    </Sheet>
  );
};
